import { parseHtml, twClassMerge } from '@healthhub/react-ui';
import { AutoComplete, Divider } from 'antd';
import DOMPurify from 'dompurify';
import { useEffect, useRef, useState } from 'react';

import { sitecoreHomeBaseUrl } from '@shared/configs/configs/config';
import useResponsive from '@shared/hooks/useResponsive';
import { useGetArticlesByTextQuery } from '@shared/services/sitecore';
import { boldSearchText } from '@shared/utils/string';

import SearchInput from './SearchInput';
import styles from './styles.module.css';

interface GlobalSearchInputProps {
  isOpen?: boolean;
}

const VIEW_ALL_OPTION = 'viewAll';

const GlobalSearchInput = ({ isOpen }: GlobalSearchInputProps) => {
  // Unable to use the ref type of AutoComplete
  const autoCompleteRef = useRef<any>(null);

  const [searchText, setSearchText] = useState('');
  const [isFocus, setIsFocus] = useState(false);
  const [options, setOptions] = useState<{ label: JSX.Element; value: string }[]>([]);

  const { data: articlesData } = useGetArticlesByTextQuery(
    { Query: searchText },
    { skip: !searchText || searchText.length < 3 }
  );
  const responsive = useResponsive();

  const searchUrl = `${sitecoreHomeBaseUrl()}/search#site-search_e=0&site-search_q=${searchText}`;

  const defaultOptions = {
    label: <Divider className="m-0 border border-dashed border-primary-green-4" />,
    value: 'divider',
    options: [
      {
        label: (
          <a className="font-semibold text-primary-green-4" href={searchUrl} rel="noopener noreferrer" target="_blank">
            View all results
          </a>
        ),
        value: VIEW_ALL_OPTION,
      },
    ],
  };
  const mergedOptions: {
    label: JSX.Element;
    options?: {
      label: JSX.Element;
      value: string;
    }[];
    value: string;
  }[] = options.length > 0 ? [...options, defaultOptions] : [];

  const openSearchPage = () => {
    window.open(searchUrl, '_blank', 'noopener,noreferrer');
  };

  useEffect(() => {
    if (articlesData) {
      setOptions(
        articlesData.Results.map((article) => ({
          label: (
            <a href={article.Url} rel="noopener noreferrer" target="_blank">
              {parseHtml(DOMPurify.sanitize(boldSearchText(searchText, article.Title || '')))}
            </a>
          ),
          value: article.Id,
        }))
      );
    }
  }, [articlesData, searchText]);

  useEffect(() => {
    if (!searchText || searchText.length < 3) {
      setOptions([]);
    }
  }, [searchText]);

  return (
    <div
      className={twClassMerge(
        'absolute hidden z-globalSearch group inset-x-0 top-full px-4 py-10 lg:p-0 bg-white lg:bg-none rounded-b-lg lg:static lg:block shadow-portal-card lg:shadow-none',
        isOpen && 'block'
      )}
      id="global-search-input"
    >
      {responsive.lg ? (
        <AutoComplete
          className="[&_.ant-select-selector]:border-0"
          classNames={{
            popup: {
              root: styles.autoComplete,
            },
          }}
          getPopupContainer={() => document.getElementById('global-search-input')!}
          onBlur={() => {
            setIsFocus(false);
          }}
          onFocus={() => {
            setIsFocus(true);
          }}
          options={mergedOptions}
          ref={autoCompleteRef}
          value={searchText}
        >
          <SearchInput
            isFocus={isFocus}
            onBlur={() => autoCompleteRef.current?.blur?.()}
            openSearchPage={openSearchPage}
            updateSearchText={(text) => setSearchText(text)}
          />
        </AutoComplete>
      ) : (
        <div className="flex flex-col gap-2">
          <SearchInput
            isFocus={isFocus}
            onInputBlur={() => setIsFocus(false)}
            onInputFocus={() => {
              setIsFocus(true);
            }}
            openSearchPage={openSearchPage}
            updateSearchText={(text) => setSearchText(text)}
            value={searchText}
          />
          <div className="flex flex-col gap-2 px-2">
            {mergedOptions.map((option) => {
              if ('options' in option) {
                return (
                  <div className="flex flex-col gap-2" key={option.value}>
                    {option.label}
                    {option.options?.[0].label}
                  </div>
                );
              }

              return option.label;
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalSearchInput;
